/**
 * Agenda Navigation Utilities (P17)
 * 
 * Single source of truth para parâmetros de deep-link na Agenda.
 * Usado por: P15 (Championships), P16 (Training Plans), AgendaPage.
 */

/**
 * Parse URL search params para filtros da Agenda
 * @param {URLSearchParams} searchParams - from useSearchParams()
 * @returns {Object} Normalized filter state
 */
export const parseAgendaQuery = (searchParams) => {
  const filters = {
    // Entity filters
    championship_id: searchParams.get('championship_id') ? parseInt(searchParams.get('championship_id')) : null,
    plan_id: searchParams.get('plan_id') ? parseInt(searchParams.get('plan_id')) : null,
    
    // Date range (YYYY-MM-DD format)
    from: searchParams.get('from') || null,
    to: searchParams.get('to') || null,
    
    // Event type filter
    type: searchParams.get('type') || null, // 'TRAINING' | 'COMPETITION' | 'AGENDA_EVENT'
    
    // Origin tracking (for "back" navigation)
    origin: searchParams.get('origin') || null, // 'coach_training' | 'my_championships'
    origin_id: searchParams.get('origin_id') ? parseInt(searchParams.get('origin_id')) : null,
  };

  // Validate date formats
  if (filters.from && !isValidDate(filters.from)) {
    console.warn('[AgendaNav] Invalid from date:', filters.from);
    filters.from = null;
  }
  if (filters.to && !isValidDate(filters.to)) {
    console.warn('[AgendaNav] Invalid to date:', filters.to);
    filters.to = null;
  }

  // Ensure from <= to
  if (filters.from && filters.to && filters.from > filters.to) {
    console.warn('[AgendaNav] from > to, swapping:', filters.from, filters.to);
    [filters.from, filters.to] = [filters.to, filters.from];
  }

  return filters;
};

/**
 * Convert filter state to URL query string
 * @param {Object} filters - Filter state from parseAgendaQuery()
 * @returns {string} Query string (without leading ?)
 */
export const toAgendaQuery = (filters) => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });
  
  return params.toString();
};

/**
 * Build Agenda URL with filters
 * @param {Object} filters - Partial filter object
 * @returns {string} Full URL path with query
 */
export const buildAgendaUrl = (filters) => {
  const query = toAgendaQuery(filters);
  return query ? `/agenda?${query}` : '/agenda';
};

/**
 * Get human-readable label for origin
 * @param {string} origin - Origin key
 * @returns {string} Display label
 */
export const getOriginLabel = (origin) => {
  const labels = {
    'coach_training': 'Planos de Treino',
    'my_championships': 'Meus Campeonatos',
    'calendar': 'Calendário',
    'feed': 'Feed',
  };
  return labels[origin] || 'Navegação';
};

/**
 * Get origin route for "back" button
 * @param {string} origin - Origin key
 * @param {number|null} originId - Entity ID
 * @returns {string} Route path
 */
export const getOriginRoute = (origin, originId) => {
  const routes = {
    'coach_training': '/treinos', // consolidação ago/2026 (era /coach/training)
    'my_championships': '/meus-campeonatos',
    'calendar': '/agenda', // fallback to clean agenda
    'feed': '/feed',
  };
  return routes[origin] || '/feed';
};

/**
 * Validate ISO date format (YYYY-MM-DD)
 * @param {string} dateStr
 * @returns {boolean}
 */
const isValidDate = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

/**
 * Get default date range (fallback when not provided)
 * @returns {Object} { from, to } in YYYY-MM-DD
 */
export const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 7); // 7 days ago
  const to = new Date(today);
  to.setDate(to.getDate() + 30); // 30 days ahead
  
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0],
  };
};

/**
 * Build filter summary for UI display
 * @param {Object} filters - Parsed filters
 * @returns {Array<Object>} { label, value, type }
 */
export const buildFilterSummary = (filters) => {
  const summary = [];
  
  if (filters.championship_id) {
    summary.push({
      type: 'championship',
      label: 'Campeonato',
      value: `ID ${filters.championship_id}`,
      removable: true,
    });
  }
  
  if (filters.plan_id) {
    summary.push({
      type: 'plan',
      label: 'Plano de Treino',
      value: `ID ${filters.plan_id}`,
      removable: true,
    });
  }
  
  if (filters.type) {
    summary.push({
      type: 'event_type',
      label: 'Tipo',
      value: filters.type === 'TRAINING' ? 'Treinos' : 
             filters.type === 'COMPETITION' ? 'Competições' : 'Eventos',
      removable: true,
    });
  }
  
  if (filters.from && filters.to) {
    const fromDate = new Date(filters.from);
    const toDate = new Date(filters.to);
    const days = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));
    summary.push({
      type: 'date_range',
      label: 'Período',
      value: `${days} dias`,
      removable: false, // Date range não é removível (sempre precisa de range)
    });
  }
  
  return summary;
};
